import { useMemo, useState } from 'react';

import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import { folderColumn } from 'helpers/folderPaths';
import TableViewLayout from 'layouts/TableViewLayout';

import { fetchRecurrenceDefinitions, type RecurrenceDefinition } from '../../../../redux/slices/recurrenceDefinitionsSlice';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { getActions, ActionButton, ActionDropdown, type ActionEntity } from 'actions';

const toActionEntity = (row: RecurrenceDefinition): ActionEntity => ({
  type: 'recurrence_definition',
  id: row.recurrence_definition_id,
  zoneName: row.zone_name,
  name: row.name,
});

const ListRecurrenceDefinitions = () => {
  const dispatch = useAppDispatch();

  const userData = useAppSelector((state) => state.auth.userData);
  const { nodes, config: headerConfig, filtersConfig, pagination, error, loading } = useAppSelector((state) => state.recurrenceDefinitions);

  const isLoading = loading?.fetchRecurrenceDefinitions ?? false;

  const pageName = 'recurrence-definitions';
  const [showSidebar, setShowSidebar] = useState(false);

  const refreshList = () => {
    dispatch(fetchRecurrenceDefinitions({ page, limit }));
  };

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    page,
    updateUserSettings,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    fetchData: fetchRecurrenceDefinitions,
    columnFormats: { created_at: 'datetime', updated_at: 'datetime' },
    getFormattedRow: (row: RecurrenceDefinition) => {
      // The backend formats the (possibly multiple) recurrence rules into a
      // single summary string for this column.
      return {
        ...row,
        recurrence_display: row.recurrence_display ?? '',
      };
    },
  });

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const actions = getActions({ entities: [toActionEntity(row.original as RecurrenceDefinition)], refresh: refreshList });
      return <ActionDropdown actions={actions} items={['edit', 'duplicate', 'addToTransportRecurrenceDefinition', 'delete']} />;
    },
  };

  const baseColumns = generateColumns((key, order) => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });

  const columns = [...baseColumns, folderColumn, actionsColumn];

  const formattedData = useMemo(() => getFormattedData(nodes), [nodes]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: true,
    getRowId: (row) => String(row.recurrence_definition_id),
  });

  // One resolution against the current multi-selection: the create static is
  // always present (ignores the selection); addToTransport appears once at
  // least one row is selected.
  const selectionEntities = table.getSelectedRowModel().rows.map((r: any) => toActionEntity(r.original as RecurrenceDefinition));
  const toolbarActions = getActions({ entities: selectionEntities, refresh: refreshList });

  return (
    <TableViewLayout
      filtersConfig={filtersConfig}
      filters={filters}
      pageName={pageName}
      columns={headerConfig.map((c) => ({
        key: c.header_key,
        label: c.label,
      }))}
      pageSize={limit}
      error={error}
      onFilterChange={setFilterAndSync}
      onResetFilters={resetFilters}
      onPageSizeChange={updateLimit}
      currentPage={pagination.currentPage}
      totalPages={pagination.totalPages}
      totalItems={pagination.totalItems}
      onPageChange={updatePage}
      table={table}
      showSidebar={showSidebar}
      setShowSidebar={setShowSidebar}
      onRefresh={refresh}
      isLoading={isLoading}
      isSilentRefresh={isSilentRefresh}
      toolbarButtons={
        <>
          <ActionButton actions={toolbarActions} action="createRecurrenceDefinition" />
          <ActionButton
            actions={toolbarActions}
            action="addToTransportRecurrenceDefinition"
            className="ms-2"
            variant="secondary"
            label="Add selected to transport"
          />
        </>
      }
    />
  );
};

export default ListRecurrenceDefinitions;
